import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EvidenceMetric } from '@/api/client';
import { parseFormattedText } from '@/lib/textFormatting';

interface EvidenceMetricsCarouselProps {
  metrics: EvidenceMetric[];
}

// Format bytes to human readable
function formatBytes(bytes: number): string {
  // Handle edge cases: NaN, Infinity, zero, negative
  if (!Number.isFinite(bytes) || bytes === 0) return '0 B';

  const sign = bytes < 0 ? '-' : '';
  const absBytes = Math.abs(bytes);

  // Handle sub-byte values (0 < bytes < 1)
  if (absBytes < 1) {
    return sign + absBytes.toFixed(3) + ' B';
  }

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(absBytes) / Math.log(k));
  // Clamp index to valid range
  const safeIndex = Math.max(0, Math.min(i, sizes.length - 1));
  return sign + parseFloat((absBytes / Math.pow(k, safeIndex)).toFixed(2)) + ' ' + sizes[safeIndex];
}

// Format value based on unit
function formatValue(value: number, unit: string): string {
  if (unit === 'bytes' || unit === 'By') {
    return formatBytes(value);
  }
  if (unit === 'percent' || unit === '%') {
    return value.toFixed(1) + '%';
  }
  if (unit === 'milliseconds' || unit === 'ms') {
    return value.toFixed(0) + 'ms';
  }
  if (unit === 'seconds' || unit === 's') {
    return value.toFixed(2) + 's';
  }
  // Default: format with reasonable precision
  if (value >= 1000000) {
    return (value / 1000000).toFixed(2) + 'M';
  }
  if (value >= 1000) {
    return (value / 1000).toFixed(2) + 'K';
  }
  return value.toFixed(2);
}

// Custom tooltip component
interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; payload: { fullTimestamp: string } }>;
  unit: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, unit }) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    return (
      <div className="bg-background border rounded-md shadow-lg p-2 text-sm">
        <p className="font-medium">{formatValue(data.value, unit)}</p>
        <p className="text-muted-foreground text-xs">
          {new Date(data.payload.fullTimestamp).toLocaleString([], { timeZone: 'UTC' })} UTC
        </p>
      </div>
    );
  }
  return null;
};

export const EvidenceMetricsCarousel: React.FC<EvidenceMetricsCarouselProps> = ({
  metrics,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Clamp currentIndex when metrics array changes to prevent out-of-bounds access
  useEffect(() => {
    if (metrics && metrics.length > 0 && currentIndex >= metrics.length) {
      setCurrentIndex(Math.max(0, metrics.length - 1));
    } else if (!metrics || metrics.length === 0) {
      setCurrentIndex(0);
    }
  }, [metrics, currentIndex]);

  if (!metrics || metrics.length === 0) {
    return null;
  }

  // Compute safe index to prevent out-of-bounds access before useEffect can clamp
  const safeIndex = Math.max(0, Math.min(currentIndex, metrics.length - 1));
  const metric = metrics[safeIndex];

  // Format data for Recharts (handle null/undefined data_points)
  // Use UTC to stay consistent with incident detection/analysis timestamps
  const chartData = (metric.data_points || []).map((dp) => ({
    time: new Date(dp.timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
    }),
    value: dp.value,
    fullTimestamp: dp.timestamp,
  }));

  // Find annotation timestamps for reference lines (handle null/undefined annotations)
  const annotations = metric.annotations || [];
  const annotationTimes = annotations.map((ann) =>
    new Date(ann.timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
    })
  );

  const handlePrev = () => {
    setCurrentIndex((i) => Math.max(0, i - 1));
  };

  const handleNext = () => {
    setCurrentIndex((i) => Math.min(metrics.length - 1, i + 1));
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div className="flex-1 min-w-0">
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="truncate">{metric.display_name}</span>
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {parseFormattedText(metric.significance)}
            </p>
            {metric.workload_name && (
              <p className="text-xs text-muted-foreground mt-1">
                <span className="font-medium">{metric.namespace}</span>
                {' / '}
                <span>{metric.workload_name}</span>
                {metric.pod_name && (
                  <>
                    {' / '}
                    <span className="font-mono">{metric.pod_name}</span>
                  </>
                )}
              </p>
            )}
          </div>
          {metrics.length > 1 && (
            <div className="flex items-center gap-2 ml-4 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrev}
                disabled={safeIndex === 0}
                className="h-8 w-8 p-0"
                aria-label="Previous metric"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {safeIndex + 1} / {metrics.length}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNext}
                disabled={safeIndex === metrics.length - 1}
                className="h-8 w-8 p-0"
                aria-label="Next metric"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="time"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                className="text-muted-foreground"
              />
              <YAxis
                fontSize={11}
                tickLine={false}
                axisLine={false}
                className="text-muted-foreground"
                tickFormatter={(value) => formatValue(value, metric.unit)}
                width={60}
              />
              <Tooltip content={<CustomTooltip unit={metric.unit} />} />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: '#3b82f6' }}
              />
              {/* Annotation reference lines */}
              {annotationTimes.map((time, i) => (
                <ReferenceLine
                  key={i}
                  x={time}
                  stroke="#ef4444"
                  strokeDasharray="5 5"
                  strokeWidth={1.5}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[250px] flex items-center justify-center text-muted-foreground">
            No data points available
          </div>
        )}

        {/* Annotation legend */}
        {annotations.length > 0 && (
          <div className="mt-4 space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">Key Points</h4>
            {annotations.map((ann, i) => (
              <div
                key={i}
                className="flex items-start gap-2 p-2 bg-muted rounded-md text-sm"
              >
                <div className="w-2 h-2 bg-red-500 rounded-full mt-1.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{ann.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(ann.timestamp).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                        timeZone: 'UTC',
                      })} UTC
                    </span>
                  </div>
                  <p className="text-muted-foreground text-sm">{parseFormattedText(ann.description)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EvidenceMetricsCarousel;
